import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box, Card, CardContent, Typography, Button, Stack, MenuItem,
    TextField, Alert, IconButton, LinearProgress,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import SaveIcon from '@mui/icons-material/Save';
import { createSession, getLocalCourses } from '../../services/trainingSessionService';

export default function TrainingSessionForm() {
    const navigate = useNavigate();

    const [rawCourses, setRawCourses] = useState([]);
    const [coursesLoaded, setCoursesLoaded] = useState(false);
    const [courseId, setCourseId] = useState('');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        getLocalCourses().then(list => {
            setRawCourses(list || []);
            setCoursesLoaded(true);
        });
    }, []);

    const courses = useMemo(
        () => (rawCourses || []).filter(c => c && c.id != null),
        [rawCourses]
    );
    const selectedCourse = useMemo(
        () => courses.find(c => String(c.id) === String(courseId)),
        [courses, courseId]
    );

    const handleSave = async () => {
        if (!courseId) { setError('Select a course'); return; }
        setError(null);
        setSaving(true);
        try {
            const resp = await createSession({
                courseId: selectedCourse?.id ?? null,
                language: 'en',  // English по умолчанию; на public странице юзер сам выберет если есть оба языка.
            });
            navigate(`/training-sessions/${resp.uuid}`);
        } catch (e) {
            setError(e?.response?.data?.error || e.message || 'Failed to create session');
        } finally {
            setSaving(false);
        }
    };

    let helper = 'Loading…';
    if (coursesLoaded) {
        if (rawCourses.length === 0) {
            helper = 'No courses in local DB. Run Sync to download them.';
        } else if (courses.length === 0) {
            helper = `${rawCourses.length} item(s) in IDB but none usable — re-run Sync.`;
        } else {
            helper = `${courses.length} course(s) available`;
        }
    }

    return (
        <Box sx={{
            position: 'fixed', top: '64px', left: 0, right: 0, bottom: 0,
            display: 'flex', flexDirection: 'column',
            overflow: 'auto',
            backgroundColor: '#f5f5f5',
        }}>
            <Box sx={{
                backgroundColor: 'white',
                borderBottom: '1px solid #e0e0e0',
                p: 2,
                flex: '0 0 auto',
                display: 'flex',
                alignItems: 'center',
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                    <IconButton onClick={() => navigate('/training-sessions')} edge="start">
                        <ArrowBackIcon />
                    </IconButton>
                    <Box>
                        <Typography variant="h6" fontWeight={600}>New Training Session</Typography>
                        <Typography variant="caption" color="text.secondary">
                            Pick a course — you'll add participants on the next step.
                        </Typography>
                    </Box>
                </Box>
            </Box>

            {saving && <LinearProgress />}

            <Box sx={{ p: { xs: 2, sm: 3 }, display: 'flex', justifyContent: 'center' }}>
                <Card sx={{ maxWidth: 640, width: '100%' }}>
                    <CardContent>
                        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                        <Stack spacing={2.5}>
                            <TextField
                                select
                                label="Course"
                                value={courseId}
                                onChange={(e) => setCourseId(e.target.value)}
                                helperText={helper}
                                required
                                fullWidth
                                disabled={!coursesLoaded || courses.length === 0}
                            >
                                {courses.map((c) => {
                                    const langs = Array.isArray(c.publishedQuizLanguages) ? c.publishedQuizLanguages : [];
                                    return (
                                        <MenuItem key={c.id} value={String(c.id)}>
                                            {c.course}
                                            {langs.length > 0
                                                ? `  (${langs.map(l => l.toUpperCase()).join(', ')})`
                                                : '  · no published quiz'}
                                        </MenuItem>
                                    );
                                })}
                            </TextField>

                            <Button
                                variant="contained"
                                onClick={handleSave}
                                disabled={saving || !courseId}
                                startIcon={<SaveIcon />}
                                size="large"
                                sx={{ alignSelf: 'flex-start' }}
                            >
                                Create session
                            </Button>
                        </Stack>
                    </CardContent>
                </Card>
            </Box>
        </Box>
    );
}
